import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  GridDataResult,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import {
  ColumnOptions,
  GridState,
  GridStateCompositeFilterDescriptor,
  MessageService,
  LoaderService,
  NamedEntity,
} from '@shared';
import { TiersService } from '@shared-data';
import { ReportService } from '../../../services';
import { GridRowPricingException } from '../../../models';

@Component({
  selector: 'app-pricing-exceptions',
  templateUrl: './pricing-exceptions.component.html',
  styleUrls: ['./pricing-exceptions.component.scss'],
})
export class PricingExceptionsComponent {
  pricingExceptions: GridDataResult = { data: [], total: 0 };

  columns: ColumnOptions[] = [];

  state!: GridState;

  pageSize: number = 10;

  fontSize: number = 0.875;

  initialSort: SortDescriptor[] = [
    { field: 'tierName', dir: 'asc' },
    { field: 'restaurantName', dir: 'asc' },
    { field: 'sortOrder', dir: 'asc' },
    { field: 'itemName', dir: 'asc' },
  ];

  reportType!: string;

  tiers: NamedEntity[] = [];
  selectedTier: NamedEntity = new NamedEntity('-1', 'All Tiers');

  itemTypes: NamedEntity[] = [
    new NamedEntity('', 'All'),
    new NamedEntity('A La Carte', 'A La Carte'),
    new NamedEntity('Combo Component', 'Combo Components'),
    new NamedEntity('Combo Entree', 'Combo Entree'),
  ];
  selectedItemType: NamedEntity = this.itemTypes[0];

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private tiersService: TiersService,
    private route: ActivatedRoute,
  ) {
    this.columns = new GridRowPricingException().getColumns();
  }

  ngOnInit() {
    this.subscribeToReportChange();
  }

  subscribeToReportChange() {
    this.route.params.subscribe((params) => {
      this.reportType = params['type'];
      this.getTiers();
    });
  }

  getTiers() {
    this.loaderService.show();
    this.tiersService.getTiers().subscribe({
      next: (tiers) => {
        this.tiers = tiers.map((tier) => new NamedEntity(tier.id, tier.name));
        this.tiers.unshift(new NamedEntity('-1', 'All Tiers'));
        this.selectedTier = this.tiers[0];
        this.loaderService.hide();
        this.setDefaultState();
      },
      error: (err) => {
        this.messageService.error(`Failed to get tiers: ${err.error}`);
        this.loaderService.hide();
      },
    });
  }

  setDefaultState(): void {
    this.state = new GridState(this.pageSize, this.initialSort);
    this.fetchReportData();
  }

  onToolbarFilterChange(): void {
    this.state.skip = 0;
    this.fetchReportData();
  }

  fetchReportData() {
    this.loaderService.show();

    if (this.state.filter) {
      this.state.filter = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(
          this.state.filter,
          'tierName',
          'itemType',
        ),
      );
    }

    if (this.selectedTier.id != '-1') {
      if (!this.state.filter) {
        this.state.filter = new GridStateCompositeFilterDescriptor();
      }
      this.state.filter = this.state.filter.and({
        field: 'tierName',
        operator: 'eq',
        value: this.selectedTier.name,
      });
    }

    if (this.selectedItemType.id != '') {
      if (!this.state.filter) {
        this.state.filter = new GridStateCompositeFilterDescriptor();
      }
      this.state.filter = this.state.filter.and({
        field: 'itemType',
        operator: 'eq',
        value: this.selectedItemType.id,
      });
    }

    this.reportService
      .getReportData(`pricing/${this.reportType}`, this.state)
      .subscribe({
        next: (data) => {
          this.pricingExceptions = data;

          this.pricingExceptions.data = this.pricingExceptions.data.map(
            (item) =>
              new GridRowPricingException(
                item.parItemId,
                item.plu,
                item.itemName,
                item.itemPrice,
                item.restaurantName,
                item.tierName,
                item.itemType,
                item.isActive,
                item.deliveryPrice,
              ),
          );
          this.loaderService.hide();
        },
        error: (err) => {
          this.messageService.error(err);
          this.loaderService.hide();
        },
      });
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.state = event as GridState;

    this.fetchReportData();
  }

  exportExceptions() {
    this.loaderService.show();
    this.reportService
      .exportExceptions(`pricing/${this.reportType}`)
      .subscribe({
        next: (data: Blob) => {
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Pricing_Exceptions_${new Date().toDateString()}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.loaderService.hide();
        },
        error: (err: string) => {
          this.loaderService.hide();
          this.messageService.error(err);
        },
      });
  }
}
